import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { normalizeMonthIds } from "../assets/helpers";

const PORTFOLIOS_API_URL = "portfolio/portfolios/";
const BANK_DASHBOARD_API_URL = "portfolio/bank-dashboard/";
const BANK_OVERVIEW_API_URL = "portfolio/teamlead-overview";
const PORTFOLIO_FILES_API_URL = "portfolio/portfolio-uploaded-files";
const CASE_INTENSITY_API_URL = "agency/case-intensity-counts/";
const MONTHLY_PTP_API_URL = "agency/weekly-ptp/";
const WEEKLY_PAID_API_URL = "agency/weekly-paid/";
const CASE_TREND_API_URL = "portfolio/case-trend/";
const TEAMLEAD_ALLOCATION_API_URL = "portfolio/teamlead-agent-allocation-breakdown/";

export const fetchPortfolios = createAsyncThunk(
  "portfolio/fetchPortfolios",
  async (company_id, thunkAPI) => {
    try {
      const state = thunkAPI.getState();
      const token = state.auth.token;

      if (!token) {
        return [];
      }

      const url = company_id
        ? `${PORTFOLIOS_API_URL}?company_id=${company_id}`
        : PORTFOLIOS_API_URL;

      const response = await axios.get(url, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      return response?.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message ||
        error.message ||
        "Failed to fetch portfolios"
      );
    }
  }
);
export const fetchBankDashboard = createAsyncThunk(
  "portfolio/fetchBankDashboard",
  async (_, thunkAPI) => {
    try {
      const state = thunkAPI.getState();
      const token = state.auth.token;

      const validUser = state.auth.user;
      if (!token || (!validUser?.is_agency && validUser?.is_supervisor) || validUser?.is_tc) {
        return;
      }

      const response = await axios.get(BANK_DASHBOARD_API_URL, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      return response?.data?.portfolios;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message ||
        error.message ||
        "Failed to fetch bank dashboard"
      );
    }
  }
);
export const fetchBankOverview = createAsyncThunk(
  "portfolio/fetchBankOverview",
  async (param, thunkAPI) => {
    try {
      const state = thunkAPI.getState();
      const token = state.auth.token;
      const { bank_id } = param;
      const validUser = state.auth.user;

      if (!token || (!validUser?.is_agency && !validUser?.is_supervisor)) {
        return;
      }

      const queryParams = {};

      if (param?.start_date) {
        queryParams.start_date = param.start_date
      }

      if (param?.end_date) {
        queryParams.end_date = param.end_date
      }
      if (param?.products && Array(param.products) && param?.products?.length > 0) {
        queryParams.product = param.products.join(',')
      }

      const response = await axios.get(`${BANK_OVERVIEW_API_URL}/${bank_id}/`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        params: queryParams
      });

      return response?.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message ||
        error.message ||
        "Failed to fetch bank dashboard"
      );
    }
  }
);
export const fetchPortfolioFiles = createAsyncThunk(
  "portfolio/fetchUploadedfiles",
  async (param, thunkAPI) => {
    try {
      const state = thunkAPI.getState();
      const token = state.auth.token;
      const { portfolio_id } = param;

      if (!token) {
        return;
      }

      const response = await axios.get(
        `${PORTFOLIO_FILES_API_URL}/${portfolio_id}/`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      return response?.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message ||
        error.message ||
        "Failed to fetch bank dashboard"
      );
    }
  }
);

export const fetchCaseIntencity = createAsyncThunk(
  "portfolio/fetchCaseIntencity",
  async (params, thunkAPI) => {
    try {
      const state = thunkAPI.getState();
      const token = state.auth.token;
      const validUser = state.auth.user;

      if (!token) {
        return [];
      }

      const queryParams = params ? { ...params } : {};

      if (Array.isArray(params?.portfolios) && params?.portfolios.length > 0) {
        queryParams.portfolio = params.portfolios.join(',');
        delete queryParams.portfolios;
      }
      if (Array.isArray(params?.months) && params?.months.length > 0) {
        queryParams.month = normalizeMonthIds(params.months);
        delete queryParams.months;
      }
      if (Array.isArray(params?.states) && params?.states.length > 0) {
        queryParams.state = params.states.join(',');
        delete queryParams.states;
      }
      if (Array.isArray(params?.region) && params?.region.length > 0) {
        queryParams.region = params.region.join(',');
      }
      if (Array.isArray(params?.disposition) && params?.disposition.length > 0) {
        queryParams.disposition = params.disposition.join(',');
      }
      if (Array.isArray(params?.buckets) && params?.buckets.length > 0) {
        queryParams.bucket = params.buckets.join(',');
        delete queryParams.buckets;
      }
      if (Array.isArray(params?.branches) && params?.branches.length > 0) {
        queryParams.branch = params.branches.join(',');
        delete queryParams.branches;
      }
      if (Array.isArray(params?.supervisors) && params?.supervisors.length > 0) {
        queryParams.supervisor = params.supervisors.join(',');
        delete queryParams.supervisors;
      }
      if (Array.isArray(params?.teamleads) && params?.teamleads.length > 0) {
        queryParams.teamlead = params.teamleads.join(',');
        delete queryParams.teamleads;
      }
      if (Array.isArray(params?.telecallers) && params?.telecallers.length > 0) {
        queryParams.telecaller = params.telecallers.join(',');
        delete queryParams.telecallers;
      }

      const apiUrl = validUser?.is_supervisor ? "agency/team-case-intensity-counts/" : CASE_INTENSITY_API_URL;

      const response = await axios.get(apiUrl, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        params: queryParams,
      });

      return response?.data?.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message ||
        error.message ||
        "Failed to fetch portfolios"
      );
    }
  }
);

export const fetchMonthlyPtp = createAsyncThunk(
  "portfolio/fetchMonthlyPtp",
  async (params, thunkAPI) => {
    try {
      const state = thunkAPI.getState();
      const token = state.auth.token;
      const queryParams = {};
      if (params?.portfolio) {
        queryParams.portfolio = params.portfolio
      }
      if (Array.isArray(params?.months) && params?.months.length > 0) {
        queryParams.month = normalizeMonthIds(params.months);
        delete queryParams.months;
      }
      if (params?.telecaller) {
        queryParams.telecaller = params.telecaller.join(',')
      }
      if (params?.year) {
        queryParams.year = params.year
      }
      if (params?.week) {
        queryParams.week = params.week
      }
      if (!token) {
        return [];
      }

      const response = await axios.get(MONTHLY_PTP_API_URL, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,

        },
        params: queryParams
      });

      return response?.data?.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message ||
        error.message ||
        "Failed to fetch portfolios"
      );
    }
  }
);
export const fetchWeeklyPaid = createAsyncThunk(
  "portfolio/fetchWeeklyPaid",
  async (params, thunkAPI) => {
    try {
      const state = thunkAPI.getState();
      const token = state.auth.token;
      const queryParams = {};
      if (params?.portfolio) {
        queryParams.portfolio = params.portfolio
      }
      if (Array.isArray(params?.months) && params?.months.length > 0) {
        queryParams.month = normalizeMonthIds(params.months);
        delete queryParams.months;
      }
      if (params?.telecaller) {
        queryParams.telecaller = params.telecaller.join(',')
      }
      if (params?.year) {
        queryParams.year = params.year
      }
      if (params?.week) {
        queryParams.week = params.week
      }
      if (!token) {
        return [];
      }

      const response = await axios.get(WEEKLY_PAID_API_URL, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,

        },
        params: queryParams
      });

      return response?.data?.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message ||
        error.message ||
        "Failed to fetch portfolios"
      );
    }
  }
);
export const fetchCaseTrend = createAsyncThunk(
  "portfolio/fetchCaseTrend",
  async (portfolio_id, thunkAPI) => {
    try {
      const state = thunkAPI.getState();
      const token = state.auth.token;

      if (!token) {
        return null;
      }

      const response = await axios.get(`${CASE_TREND_API_URL}${portfolio_id}/`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      return response?.data?.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message ||
        error.message ||
        "Failed to fetch case trend"
      );
    }
  }
);

export const fetchAllocationBreakdown = createAsyncThunk(
  "portfolio/fetchAllocationBreakdown",
  async (portfolio_id, thunkAPI) => {
    try {
      const state = thunkAPI.getState();
      const token = state.auth.token;

      if (!token) {
        return null;
      }

      const response = await axios.get(`${TEAMLEAD_ALLOCATION_API_URL}${portfolio_id}/`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      return response?.data?.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message ||
        error.message ||
        "Failed to fetch allocation breakdown"
      );
    }
  }
);


const initialState = {
  portfolios: [],
  loading: false,
  error: null,

  bankDashboard: [],
  bankDashboard_loading: false,
  bankDashboard_error: null,

  bankOverview: [],
  bankOverviwLoading: false,
  bankOverviewError: null,

  uploaded_files: [],
  uploaded_files_loading: false,

  case_intensity_counts: [],
  case_intensity_counts_loading: false,

  Monthly_ptp: [],
  Monthly_ptp_loading: false,
  Weekly_paid: [],
  Weekly_paid_loading: false,

  case_trend: null,
  case_trend_loading: false,

  allocation_breakdown_data: null,
  allocation_breakdown_loading: false,

};

const portfolioSlice = createSlice({
  name: "portfolio",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchPortfolios.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPortfolios.fulfilled, (state, action) => {
        state.loading = false;
        state.portfolios = action.payload?.portfolios || [];
        state.error = null;
      })
      .addCase(fetchPortfolios.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch portfolios";
        state.portfolios = [];
      })

      // bank dashboard
      .addCase(fetchBankDashboard.pending, (state) => {
        state.bankDashboard_loading = true;
        state.bankDashboard_error = null;
      })
      .addCase(fetchBankDashboard.fulfilled, (state, action) => {
        state.bankDashboard_loading = false;
        state.bankDashboard = action.payload || [];
        state.bankDashboard_error = null;
      })
      .addCase(fetchBankDashboard.rejected, (state, action) => {
        state.bankDashboard_loading = false;
        state.bankDashboard_error =
          action.payload || "Failed to fetch bank dashboard";
        state.bankDashboard = [];
      })

      // bank wise overview
      .addCase(fetchBankOverview.pending, (state) => {
        state.bankOverviwLoading = true;
        state.bankOverviewError = null;
      })
      .addCase(fetchBankOverview.fulfilled, (state, action) => {
        state.bankOverviwLoading = false;
        state.bankOverview = action.payload || [];
        state.bankOverviewError = null;
      })
      .addCase(fetchBankOverview.rejected, (state, action) => {
        state.bankOverviwLoading = false;
        state.bankDashboard_error =
          action.payload || "Failed to fetch bank dashboard";
        state.bankOverview = [];
      })

      // uploaded files for portfolio
      .addCase(fetchPortfolioFiles.pending, (state) => {
        state.uploaded_files_loading = true;
      })
      .addCase(fetchPortfolioFiles.fulfilled, (state, action) => {
        state.uploaded_files_loading = false;
        state.uploaded_files = action.payload || [];
      })
      .addCase(fetchPortfolioFiles.rejected, (state, action) => {
        state.uploaded_files_loading = false;

        state.uploaded_files = [];
      })
      // case intensity counts
      .addCase(fetchCaseIntencity.pending, (state) => {
        state.case_intensity_counts_loading = true;
      })
      .addCase(fetchCaseIntencity.fulfilled, (state, action) => {
        state.case_intensity_counts_loading = false;
        state.case_intensity_counts = action.payload || [];
      })
      .addCase(fetchCaseIntencity.rejected, (state, action) => {
        state.case_intensity_counts_loading = false;
        state.case_intensity_counts = [];
      })

      // monthly ptp
      .addCase(fetchMonthlyPtp.pending, (state) => {
        state.Monthly_ptp_loading = true;
      })
      .addCase(fetchMonthlyPtp.fulfilled, (state, action) => {
        state.Monthly_ptp_loading = false;
        state.Monthly_ptp = action.payload || [];
      })
      .addCase(fetchMonthlyPtp.rejected, (state, action) => {
        state.Monthly_ptp_loading = false;
        state.Monthly_ptp = [];
      })
      // weekly paid
      .addCase(fetchWeeklyPaid.pending, (state) => {
        state.Weekly_paid_loading = true;
      })
      .addCase(fetchWeeklyPaid.fulfilled, (state, action) => {
        state.Weekly_paid_loading = false;
        state.Weekly_paid = action.payload || [];
      })
      .addCase(fetchWeeklyPaid.rejected, (state, action) => {
        state.Weekly_paid_loading = false;
        state.Weekly_paid = [];
      })
      // case trend
      .addCase(fetchCaseTrend.pending, (state) => {
        state.case_trend_loading = true;
      })
      .addCase(fetchCaseTrend.fulfilled, (state, action) => {
        state.case_trend_loading = false;
        state.case_trend = action.payload || null;
      })
      .addCase(fetchCaseTrend.rejected, (state, action) => {
        state.case_trend_loading = false;
        state.case_trend = null;
      })
      // allocation breakdown
      .addCase(fetchAllocationBreakdown.pending, (state) => {
        state.allocation_breakdown_loading = true;
      })
      .addCase(fetchAllocationBreakdown.fulfilled, (state, action) => {
        state.allocation_breakdown_loading = false;
        state.allocation_breakdown_data = action.payload || null;
      })
      .addCase(fetchAllocationBreakdown.rejected, (state, action) => {
        state.allocation_breakdown_loading = false;
        state.allocation_breakdown_data = null;
      });
  },
});

export default portfolioSlice.reducer;
